import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import KPICard from "@/components/admin/KPICard";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Search, Download, RefreshCw, ArrowLeft, CreditCard, Filter, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface BillingData {
  id: string;
  user_id: string;
  full_name: string | null;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  address_province: string | null;
  address_country: string | null;
  fiscal_code: string | null;
  vat_number: string | null;
}

interface Subscription {
  id: string;
  user_id: string;
  product_type: string;
  product_duration: string;
  status: string;
  amount_paid: number;
  currency: string;
  current_period_start: string;
  current_period_end: string;
  stripe_subscription_id: string;
  created_at: string;
  user_email?: string;
  user_full_name?: string;
}

export default function AdminSubscriptions() {
  const { t } = useTranslation();
  const { isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<string[]>([]);
  const [stats, setStats] = useState({
    totalActive: 0,
    totalRevenue: 0,
    monthlyRecurring: 0,
    churnRate: 0,
  });
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [selectedBillingData, setSelectedBillingData] = useState<BillingData | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      navigate("/");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  useEffect(() => {
    filterSubscriptions();
  }, [searchQuery, statusFilter, planFilter, subscriptions]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      
      // Fetch subscriptions
      const { data: subsData, error: subsError } = await supabase
        .from("subscriptions")
        .select("*")
        .order("created_at", { ascending: false });

      if (subsError) throw subsError;

      // Fetch user emails from auth.users via RPC or profiles
      const userIds = subsData?.map(s => s.user_id) || [];
      
      // Get profiles data
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name, business_name, professional_email")
        .in("user_id", userIds);

      if (profilesError) console.error("Error loading profiles:", profilesError);

      // Fetch fallback emails via admin edge function and build a map { userId: email }
      let emailsMap: Record<string, string> = {};
      try {
        const { data: emailResp, error: emailError } = await supabase.functions.invoke("get-users-admin", {
          body: { userIds },
        });
        if (emailError) {
          console.error("Error loading user emails via edge function:", emailError);
        }
        const users = (emailResp as any)?.users as Array<{ id: string; email: string }>;
        if (Array.isArray(users)) {
          emailsMap = users.reduce((acc, u) => {
            if (u?.id && u?.email) acc[u.id] = u.email;
            return acc;
          }, {} as Record<string, string>);
        } else {
          emailsMap = {};
        }
        console.debug("AdminSubscriptions:get-users-admin emailsMap keys", Object.keys(emailsMap).length);
      } catch (e) {
        console.error("Edge function call failed:", e);
      }

      // Enrich subscriptions with profile data
      const enrichedSubs = (subsData || []).map((sub) => {
        const profile = profilesData?.find(p => p.user_id === sub.user_id);
        
        const userEmail = emailsMap[sub.user_id] || profile?.professional_email || t("admin.subscriptions.fallback.notAvailable");
        const fullName = profile 
          ? (`${profile.first_name || ""} ${profile.last_name || ""}`.trim() || profile.business_name || t("admin.subscriptions.fallback.notAvailable"))
          : t("admin.subscriptions.fallback.notAvailable");

        return {
          ...sub,
          user_email: userEmail,
          user_full_name: fullName,
        };
      });

      setSubscriptions(enrichedSubs);
      
      // Extract unique plans
      const uniquePlans = Array.from(
        new Set(enrichedSubs.map(s => s.product_type).filter(Boolean))
      ).sort();
      setAvailablePlans(uniquePlans);
      
      calculateStats(enrichedSubs);
    } catch (error) {
      console.error("Error loading subscriptions:", error);
      toast({
        title: t("admin.subscriptions.messages.error"),
        description: t("admin.subscriptions.messages.loadError"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Subscription[]) => {
    const active = data.filter((s) => s.status === "active").length;
    const revenue = data.reduce((sum, s) => sum + Number(s.amount_paid || 0), 0);
    const monthly = data
      .filter((s) => s.status === "active" && s.product_duration === "monthly")
      .reduce((sum, s) => sum + Number(s.amount_paid || 0), 0);

    setStats({
      totalActive: active,
      totalRevenue: revenue,
      monthlyRecurring: monthly,
      churnRate: 5.2, // Mock value
    });
  };

  const filterSubscriptions = () => {
    let filtered = subscriptions;

    if (statusFilter !== "all") {
      filtered = filtered.filter((s) => s.status === statusFilter);
    }

    if (planFilter !== "all") {
      filtered = filtered.filter((s) => s.product_type === planFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (s) =>
          s.user_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.stripe_subscription_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.user_full_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredSubscriptions(filtered);
  };

  const toggleFilters = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };

  const applyFilters = () => {
    filterSubscriptions();
  };

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setPlanFilter("all");
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (planFilter !== "all") count++;
    return count;
  };

  const handleViewBillingData = async (userId: string) => {
    setLoadingBilling(true);
    setBillingModalOpen(true);
    setSelectedBillingData(null);
    
    try {
      const { data, error } = await supabase
        .from("billing_data")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      setSelectedBillingData(data);
    } catch (error) {
      console.error("Error loading billing data:", error);
      toast({
        title: t("common.error"),
        description: t("admin.subscriptions.messages.billingLoadError"),
        variant: "error",
      });
    } finally {
      setLoadingBilling(false);
    }
  };

  const exportData = () => {
    if (filteredSubscriptions.length === 0) {
      toast({
        title: t("admin.subscriptions.messages.noData"),
        description: t("admin.subscriptions.messages.noSubscriptionsToExport"),
        variant: "warning",
      });
      return;
    }

    try {
    const headers = [
      t('admin.subscriptions.export.headers.fullName'),
      t('admin.subscriptions.export.headers.email'),
      t('admin.subscriptions.export.headers.plan'),
      t('admin.subscriptions.export.headers.duration'),
      t('admin.subscriptions.export.headers.status'),
      t('admin.subscriptions.export.headers.amount'),
      t('admin.subscriptions.export.headers.currency'),
      t('admin.subscriptions.export.headers.period')
    ];
    const rows = filteredSubscriptions.map((s) => [
      s.user_full_name || t("admin.subscriptions.fallback.notAvailable"),
      s.user_email || t("admin.subscriptions.fallback.notAvailable"),
      s.product_type,
      s.product_duration,
      s.status,
      s.amount_paid,
      s.currency,
      s.current_period_start && s.current_period_end
        ? `${format(new Date(s.current_period_start), "dd/MM/yyyy", { locale: it })} - ${format(new Date(s.current_period_end), "dd/MM/yyyy", { locale: it })}`
        : t("admin.subscriptions.fallback.notAvailable"),
    ]);

      const escapeCsvCell = (cell: string) => `"${cell.replace(/"/g, '""')}"`;
      
      const csvContent = [
        headers.map(escapeCsvCell).join(','),
        ...rows.map(row => row.map(cell => escapeCsvCell(String(cell))).join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `abbonamenti_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast({
        title: t("admin.subscriptions.messages.exportComplete"),
        description: t("admin.subscriptions.messages.csvDownloaded", { count: filteredSubscriptions.length }),
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast({
        title: t("admin.subscriptions.messages.error"),
        description: t("admin.subscriptions.messages.exportError"),
        variant: "error",
      });
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.subscriptions.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.subscriptions.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <CreditCard className="w-10 h-10 text-purple-600" />
              {t("admin.subscriptions.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.subscriptions.subtitle")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <KPICard title={t("admin.subscriptions.kpi.activeSubscriptions")} value={stats.totalActive} trend="up" change={12} />
          <KPICard title={t("admin.subscriptions.kpi.totalRevenue")} value={`€${stats.totalRevenue.toFixed(2)}`} trend="up" change={8} />
          <KPICard title={t("admin.subscriptions.kpi.mrr")} value={`€${stats.monthlyRecurring.toFixed(2)}`} trend="up" change={5} />
          <KPICard title={t("admin.subscriptions.kpi.churnRate")} value={`${stats.churnRate}%`} trend="down" change={2} inverse />
        </div>

        {/* Filters */}
        <BrutalCard>
          <div className="space-y-4">
            {/* Riga principale sempre visibile */}
            <div className="flex gap-4 items-center">
              {/* Barra ricerca estesa */}
              <div className="flex-1">
                <BrutalInput
                  placeholder={t("admin.subscriptions.filters.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Bottone filtri con badge count */}
              <button
                onClick={toggleFilters}
                className="relative p-3 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                aria-label={t("admin.subscriptions.filters.showFilters")}
              >
                <Filter className="w-5 h-5" />
                {getActiveFiltersCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-dark font-bold text-xs w-5 h-5 rounded-full flex items-center justify-center border-[2px] border-dark">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>

              {/* Bottone Export CSV */}
              <button
                onClick={exportData}
                className="p-3 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                aria-label={t("admin.subscriptions.filters.downloadCsv")}
                title={t("admin.subscriptions.filters.downloadCsvShort")}
              >
                <Download className="w-5 h-5" />
              </button>
            </div>

            {/* Pannello filtri espandibile */}
            {isFiltersOpen && (
              <div className="space-y-4 pt-4 border-t-[3px] border-dark">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Select Status */}
                  <div>
                    <label className="text-sm font-bold mb-2 block">{t("admin.subscriptions.filters.statusLabel")}</label>
                    <select
                      className="w-full border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">{t("admin.subscriptions.filters.allStatuses")}</option>
                      <option value="active">{t("admin.subscriptions.filters.active")}</option>
                      <option value="canceled">{t("admin.subscriptions.filters.canceled")}</option>
                      <option value="past_due">{t("admin.subscriptions.filters.pastDue")}</option>
                      <option value="trialing">{t("admin.subscriptions.filters.trialing")}</option>
                    </select>
                  </div>

                  {/* Select Plan */}
                  <div>
                    <label className="text-sm font-bold mb-2 block">{t("admin.subscriptions.filters.planLabel")}</label>
                    <select
                      className="w-full border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
                      value={planFilter}
                      onChange={(e) => setPlanFilter(e.target.value)}
                    >
                      <option value="all">{t("admin.subscriptions.filters.allPlans")}</option>
                      {availablePlans.map((plan) => (
                        <option key={plan} value={plan}>
                          {plan.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Bottoni azione */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={resetFilters}
                    className="px-6 py-3 border-[3px] border-dark font-heading font-bold uppercase tracking-wider bg-card text-foreground shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark transition-all"
                  >
                    {t("admin.subscriptions.filters.reset")}
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-6 py-3 border-[3px] border-dark font-heading font-bold uppercase tracking-wider bg-primary text-dark shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark transition-all"
                  >
                    {t("admin.subscriptions.filters.apply")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </BrutalCard>

        {/* Subscriptions Table */}
        <BrutalCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b-[3px] border-dark">
                <tr>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.subscriptions.table.fullName")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.subscriptions.table.email")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.subscriptions.table.plan")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.subscriptions.table.duration")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.subscriptions.table.status")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.subscriptions.table.amount")}
                  </th>
                    <th className="px-6 py-4 text-left font-heading font-bold">
                      {t("admin.subscriptions.table.period")}
                    </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((sub) => (
                  <tr
                    key={sub.id}
                    className="border-b-[3px] border-dark hover:bg-surface"
                  >
                    <td className="px-6 py-4">
                      {sub.user_full_name || (
                        <span className="text-muted-foreground text-sm">{t("admin.subscriptions.fallback.notAvailable")}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {sub.user_email || <span className="text-muted-foreground italic">{t("admin.subscriptions.fallback.notAvailable")}</span>}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="default" className="uppercase">
                        {sub.product_type}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline">
                        {sub.product_duration}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge
                        variant={
                          sub.status === "active"
                            ? "default"
                            : sub.status === "canceled"
                            ? "destructive"
                            : "secondary"
                        }
                        className="uppercase"
                      >
                        {sub.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 font-bold">
                      €{Number(sub.amount_paid || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {sub.current_period_start && sub.current_period_end ? (
                        <>
                          {format(new Date(sub.current_period_start), "dd/MM/yyyy", { locale: it })}
                          {" - "}
                          {format(new Date(sub.current_period_end), "dd/MM/yyyy", { locale: it })}
                        </>
                      ) : (
                        <span className="text-muted-foreground italic">
                          {t("admin.subscriptions.fallback.notAvailable")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleViewBillingData(sub.user_id)}
                                className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                aria-label={t("admin.subscriptions.table.viewBillingData")}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t("admin.subscriptions.table.viewBillingData")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => {
                                  window.location.href = `mailto:${sub.user_email}`;
                                }}
                                className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                aria-label={t("admin.subscriptions.table.contactUser")}
                              >
                                <Mail className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t("admin.subscriptions.table.contactUser")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredSubscriptions.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t("admin.subscriptions.messages.noSubscriptionsFound")}
              </div>
            )}
          </div>
        </BrutalCard>

        {/* Billing Data Modal */}
        <Dialog open={billingModalOpen} onOpenChange={setBillingModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("admin.subscriptions.billingModal.title")}</DialogTitle>
            </DialogHeader>
            
            {loadingBilling ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : selectedBillingData ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t("admin.subscriptions.billingModal.fullName")}</p>
                  <p className="font-medium">{selectedBillingData.full_name || "-"}</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground">{t("admin.subscriptions.billingModal.address")}</p>
                  <p className="font-medium">
                    {selectedBillingData.address_street || "-"}
                  </p>
                  <p className="font-medium">
                    {selectedBillingData.address_zip} {selectedBillingData.address_city}
                    {selectedBillingData.address_province && ` (${selectedBillingData.address_province})`}
                  </p>
                  <p className="font-medium">{selectedBillingData.address_country || "-"}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("admin.subscriptions.billingModal.fiscalCode")}</p>
                    <p className="font-medium font-mono">{selectedBillingData.fiscal_code || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("admin.subscriptions.billingModal.vatNumber")}</p>
                    <p className="font-medium font-mono">{selectedBillingData.vat_number || "-"}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t("admin.subscriptions.billingModal.noData")}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
